// Incident/Violation API types
import { z } from "zod";
import { pageSchema } from "@/helpers/types";

// Incident/Violation item schema (matches backend ViolationFetchResponse)
export const IncidentSchema = z.object({
  id: z.number(),
  term_message: z.string(),
  policy_id: z.number(),
  policy_name: z.string(),
  policy_term: z.string(),
  timestamp: z.string(),
  level: z.string(),
  scope: z.array(z.array(z.string())),
  contract_id: z.number(),
  contract_name: z.string(),
  last_violations_for_term: z.array(z.unknown()).nullable().optional(),
  datastore_name: z.string(),
  datastore_type: z.string(),
  status: z.string(),
  snoozed_till: z.string().nullable().optional(),
  filters: z.string(),
  actual: z.string().nullable().optional(),
  debug_copilot: z.record(z.unknown()).nullable().optional(),
});

export type Incident = z.infer<typeof IncidentSchema>;

// Get incidents params
export interface GetIncidentsParams {
  page?: number;
  size?: number;
  contract_id?: string;
  level?: string;
  start_time?: string;
  end_time?: string;
  search?: string;
  table_id?: string | number;
  levels?: string[];
  timestamp?: string;
  sortColumn?: string;
  sortOrder?: string;
  contract_ids?: string[];
  event_id?: string | number;
}

// Get incidents response schema
export const GetIncidentsResponseSchema = pageSchema(IncidentSchema);
export type GetIncidentsResponse = z.infer<typeof GetIncidentsResponseSchema>;

// Incidents summary schema (matches backend ViolationSummaryResponse)
export const IncidentsSummarySchema = z.object({
  last_violation_timestamp: z.string().nullable().optional(),
  total_error_count: z.number(),
  total_warning_count: z.number(),
  total_policy_violation_count: z.number(),
  total_validation_count: z.number(),
});

export type IncidentsSummary = z.infer<typeof IncidentsSummarySchema>;

// Get incidents summary params
export interface GetIncidentsSummaryParams {
  table_id?: string;
}

// Snooze incident params
export interface SnoozeIncidentParams {
  id: string;
  snoozed_till: string;
}

// Snooze incident response schema (matches backend ViolationSnoozeResponse)
export const SnoozeIncidentResponseSchema = z.object({
  id: z.number(),
  status: z.string(),
});

export type SnoozeIncidentResponse = z.infer<typeof SnoozeIncidentResponseSchema>;

// Timeline event schema (matches backend TimelineEventFetchResponse)
export const TimelineEventSchema = z.object({
  id: z.number(),
  contract_id: z.number(),
  event_type: z.string(),
  event_message: z.string(),
  user_first_name: z.string(),
  user_last_name: z.string(),
  user_email: z.string(),
  created_on: z.string(),
});

export type TimelineEvent = z.infer<typeof TimelineEventSchema>;

// Get timeline events response schema (matches backend TimelineEventFetchAllResponse)
export const GetTimelineEventsResponseSchema = z.object({
  timeline_events: z.array(TimelineEventSchema),
});

export type GetTimelineEventsResponse = z.infer<typeof GetTimelineEventsResponseSchema>;

// Debug SQL response schema (matches backend DebugSQLFetchResponse)
export const GetDebugSQLResponseSchema = z.object({
  debug_sql: z.string().nullable().optional(),
  status: z.string(),
  error_code: z.string().nullable().optional(),
  message: z.string().nullable().optional(),
});

export type GetDebugSQLResponse = z.infer<typeof GetDebugSQLResponseSchema>;
